import { useRef, useState } from "react";
import {
  selectNeedsActiveRecurringCheck,
  selectPlatformMethodsAvailable,
  useDonationFormStore,
} from "@/stores";
import { useTranslation } from "@/hooks/useTranslation";
import { useErrorProcessing } from "@/hooks/useErrorProcessing";
import { useNavigate } from "@/hooks/useNavigate";
import { useService } from "@/hooks/useService";
import { useTurnstile } from "@/hooks/useTurnstile";
import {
  confirmPayment,
  PAYMENT_CONFIRM_PATH,
  PAYMENT_SESSION_PATH,
} from "@/payment/paymentService";
import { awaitConfirmOutcome } from "@/payment/confirmOutcome";
import {
  isChargedPendingReviewError,
  isExpectedPaymentValidationError,
  isExpectedSubmissionRejection,
  isExpiredPaymentSession,
  isPaymentProcessingError,
  isUnknownOutcomeError,
  serverErrorBody,
} from "@/payment/paymentError";
import type { PaymentSessionStatus } from "@/payment/types";
import { usePaymentMethodsErrorsStore } from "@/payment/usePaymentMethodsErrorsStore";
import { DONOR_PORTAL_URL, PAYMENT_CONFIRM_TIMEOUT_MS } from "@/config";
import { ApiError } from "@/utils/api";
import { trackError } from "@/utils/error-monitoring";
import { withTimeout } from "@/utils/with-timeout";
import { isValidPostcodeForCountry } from "@/utils/uk-postcode";
import { dispatchEvent } from "@/utils/event-dispacher";
import { EventName } from "@/types/Events";
import { DetailsStep } from "@/types/Navigation";
import type { SubmissionSuccessResponse } from "@/types/Payment";
import type { ServerErrorResponse } from "@/types/ServerError";
import { stepForProcessor } from "./stepForProcessor";
import type { SubmissionError } from "./submissionError";
import { usePaymentSessionSync } from "./usePaymentSessionSync";

type UseDonationSubmissionArgs = {
  formId: number;
  findFirstInvalidStep: () => Promise<DetailsStep | null>;
};

export function useDonationSubmission({
  formId,
  findFirstInvalidStep,
}: UseDonationSubmissionArgs) {
  const t = useTranslation("_common");
  const [processing, setProcessing] = useState<boolean>(false);
  const [submissionError, setSubmissionError] =
    useState<SubmissionError | null>(null);
  const [showPaymentError, setShowPaymentError] = useState<boolean>(false);
  const [amountStepFailed, setAmountStepFailed] = useState<boolean>(false);
  const [isRecurringPromptVisible, setIsRecurringPromptVisible] =
    useState<boolean>(false);
  const isSubmittingRef = useRef(false);

  const { processErrors } = useErrorProcessing();
  const { next, navigateTo, visibleDetailsSteps, removeCurrentDetailsStep } =
    useNavigate();
  const sessionSync = usePaymentSessionSync({
    formId,
    onRefused: setSubmissionError,
  });
  const { checkActiveRecurring } = useService();
  const { getFreshToken, resetTurnstile } = useTurnstile();

  const {
    getSubmissionFields,
    getPayment,
    setGatewayPayload,
    setPaymentMethod,
    donationType,
    paymentMethod,
    personalDetails,
    address,
  } = useDonationFormStore();
  const platformMethodsAvailable = useDonationFormStore(
    selectPlatformMethodsAvailable,
  );
  const needsActiveRecurringCheck = useDonationFormStore(
    selectNeedsActiveRecurringCheck,
  );

  // A ref, not `processing`: state updates are async, so two taps landing in
  // the same frame (common on mobile Safari) both pass a state check and run
  // the submission twice — a second tokenization on top of the in-flight one
  // ("Another tokenization request is active") and, worse, a second POST to
  // /payment/confirm. Every entry point to a submission goes through here.
  const runExclusively = async (action: () => Promise<void>) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    try {
      await action();
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const hasActiveRecurring = async (email: string): Promise<boolean> => {
    try {
      const { active } = await checkActiveRecurring(email);
      return active;
    } catch (error) {
      // The lookup always returns 200, so a failure here is a network/server
      // issue. Don't block the donation — report it as "none found".
      trackError(error as Error);
      return false;
    }
  };

  const manageExistingRecurring = () => {
    if (!DONOR_PORTAL_URL) return;

    window.open(DONOR_PORTAL_URL, "_blank", "noopener,noreferrer");
  };

  const attemptSubmit = async () => {
    const invalidStep = await findFirstInvalidStep();

    if (invalidStep) {
      navigateTo(invalidStep);
      return;
    }

    // The current step stays open: if the donor chooses to continue,
    // `submitDonation` still has to resolve the next screen from it.
    if (needsActiveRecurringCheck) {
      setProcessing(true);
      const activeRecurring = await hasActiveRecurring(personalDetails.email);
      // Handed back here, not left to `submitDonation`: its guards return
      // before it takes over `processing`, and the donor would be sent to a
      // step inside a disabled accordion.
      setProcessing(false);

      if (activeRecurring) {
        setIsRecurringPromptVisible(true);
        return;
      }
    }

    await submitDonation();
  };

  const navigateToFailingStep = (
    errors: NonNullable<ServerErrorResponse["errors"]>,
  ): boolean => {
    const { captured, processorName } = processErrors(errors);
    const step = captured ? stepForProcessor(processorName) : null;

    if (!step) return false;

    navigateTo(step);
    return true;
  };

  // Grouped by what failed, not by stack shape: Sentry's default grouping
  // split one failure into several issues, and resolving one of them could
  // hide a later 5xx thrown from the same line.
  const trackSubmissionFailure = (
    serverError: unknown,
    submissionForTracking: Record<string, unknown> | undefined,
    body: ServerErrorResponse,
    confirmedSessionId: string | null,
  ) => {
    const validationErrorKeys =
      body?.errors && typeof body.errors === "object"
        ? Object.keys(body.errors)
        : [];
    const status =
      serverError instanceof ApiError ? serverError.status : undefined;
    const endpoint =
      confirmedSessionId === null ? PAYMENT_SESSION_PATH : PAYMENT_CONFIRM_PATH;
    // Every 410 is `session_unusable`: the reason is what tells an expired
    // session (expected) from a failed one.
    const responseErrorKey = [body?.error, body?.reason]
      .filter(Boolean)
      .join(":");

    trackError(serverError as Error, {
      payload: {
        ...submissionForTracking,
        ...(status !== undefined && { http_status: status }),
        ...(validationErrorKeys.length > 0 && {
          validation_error_keys: validationErrorKeys,
        }),
      },
      level: isExpectedSubmissionRejection(serverError) ? "warning" : "error",
      fingerprint: [
        "submission-failure",
        endpoint,
        String(status ?? (serverError as Error | undefined)?.name),
        validationErrorKeys[0] ?? (responseErrorKey || "none"),
      ],
    });
  };

  // A confirm without an answer may still have charged. Ask the backend
  // before showing the banner, so a donation that went through lands on the
  // thank-you page without a second card authorisation (#1153). The session
  // is pinned before the wait: an amount edit during the poll must not
  // replace a session that may have charged.
  const verifyLostConfirm = async (
    serverError: unknown,
    confirmedSessionId: string | null,
  ): Promise<PaymentSessionStatus | null> => {
    if (
      confirmedSessionId === null ||
      !isUnknownOutcomeError(serverError) ||
      isChargedPendingReviewError(serverError)
    ) {
      return null;
    }

    sessionSync.trackUnknownOutcome(serverError, confirmedSessionId);
    return awaitConfirmOutcome(confirmedSessionId);
  };

  const handleSubmissionError = async (
    serverError: unknown,
    submissionForTracking: Record<string, unknown> | undefined,
    confirmedSessionId: string | null,
    verifiedOutcome: PaymentSessionStatus | null,
  ) => {
    // No confirm was sent: the pre-confirm refresh failed.
    if (
      confirmedSessionId === null &&
      sessionSync.handleRefusedReplacement(serverError)
    ) {
      return;
    }

    // The backend ruled on the lost confirm without charging: the session
    // can go, exactly as after a 410 failed.
    const settledWithoutCharge =
      verifiedOutcome?.status === "failed" ||
      verifiedOutcome?.status === "expired";
    if (settledWithoutCharge) sessionSync.releaseUnresolvedSession();

    const unknownOutcome =
      !settledWithoutCharge &&
      confirmedSessionId !== null &&
      sessionSync.trackUnknownOutcome(serverError, confirmedSessionId);
    const sessionReplaced = unknownOutcome
      ? false
      : await sessionSync.replaceFailedSession();

    const body = serverErrorBody(serverError);
    dispatchEvent(EventName.PURCHASE_ERROR, body);

    if (isPaymentProcessingError(serverError)) {
      setShowPaymentError(true);
      return;
    }

    if (sessionReplaced && body?.errors && navigateToFailingStep(body.errors)) {
      return;
    }

    if (
      isChargedPendingReviewError(serverError) ||
      verifiedOutcome?.status === "charged_pending_review"
    ) {
      setSubmissionError("charged-pending-review");
      return;
    }

    let nextSubmissionError: SubmissionError = "failed";
    if (unknownOutcome) {
      nextSubmissionError = "unknown-outcome";
    } else if (
      isExpiredPaymentSession(serverError) ||
      verifiedOutcome?.status === "expired"
    ) {
      nextSubmissionError = "session-expired";
    }

    if (isExpectedPaymentValidationError(serverError)) {
      setSubmissionError(nextSubmissionError);
      return;
    }

    trackSubmissionFailure(
      serverError,
      submissionForTracking,
      body,
      confirmedSessionId,
    );
    setSubmissionError(nextSubmissionError);
  };

  const submitDonation = async () => {
    // Wallet providers can replace the address after the address step has been
    // validated. Re-check the final store value before any charge is attempted.
    if (
      !isValidPostcodeForCountry(
        String(address["postal-code"] ?? ""),
        String(address.country ?? ""),
      )
    ) {
      processErrors({
        "your_address.postal_code": [t("errors.postal-code-invalid") as string],
      });
      navigateTo(DetailsStep.YourAddress);
      return;
    }

    const payment = getPayment();
    const nonce = payment?.gatewayPayload.nonce;
    const needsGatewayAuthorisation =
      platformMethodsAvailable && paymentMethod !== "DD";

    if (
      needsGatewayAuthorisation &&
      (typeof nonce !== "string" || nonce.trim() === "")
    ) {
      usePaymentMethodsErrorsStore.getState().setErrors({
        payment: t("payment-authorisation-required") as string,
      });
      setPaymentMethod(null);
      navigateTo(DetailsStep.PaymentType);
      return;
    }

    removeCurrentDetailsStep();
    setIsRecurringPromptVisible(false);
    setProcessing(true);

    let submissionForTracking: Record<string, unknown> | undefined;
    let submissionSucceeded = false;
    let confirmedSessionId: string | null = null;
    try {
      // Use the returned session immediately rather than this render's state.
      const activeSession = await sessionSync.sessionForConfirm();
      // Read at send time: a 3DS challenge can outlive the token this render saw.
      const turnstileToken = await getFreshToken();
      const submissionFields = getSubmissionFields();

      submissionForTracking = {
        form_id: formId,
        donation_type: donationType,
        payment_method: paymentMethod,
      };

      dispatchEvent(EventName.FORM_SUBMIT, {
        form_id: formId,
        "cf-turnstile-response": turnstileToken || undefined,
        fields: submissionFields,
        ...(payment && { payment }),
      });

      confirmedSessionId = activeSession.session_id;
      const response = await withTimeout(
        (signal) =>
          confirmPayment(
            {
              form_id: formId,
              session_id: activeSession.session_id,
              fields: submissionFields,
              payment: {
                gateway_payload: payment?.gatewayPayload ?? {},
              },
              "cf-turnstile-response": turnstileToken || undefined,
            },
            signal,
          ),
        PAYMENT_CONFIRM_TIMEOUT_MS,
      );

      const typedResponse = response as {
        success: boolean;
        datalayer?: unknown;
      } & SubmissionSuccessResponse;

      if (!typedResponse.success) {
        throw new Error("Submission failed");
      }

      sessionSync.settleDonation(typedResponse);
      submissionSucceeded = true;
    } catch (serverError) {
      const outcome = await verifyLostConfirm(serverError, confirmedSessionId);

      if (outcome?.status === "confirmed" && outcome.submission_response) {
        // Still a lost confirm (timeout, proxy 5xx): keep it visible to ops.
        trackError(serverError as Error, {
          payload: {
            ...submissionForTracking,
            context: "confirm-outcome-recovered",
          },
        });
        sessionSync.settleDonation(outcome.submission_response);
        submissionSucceeded = true;
      } else {
        await handleSubmissionError(
          serverError,
          submissionForTracking,
          confirmedSessionId,
          outcome,
        );
      }
    } finally {
      // The gateway payload (e.g. a Braintree nonce) is single-use and is
      // consumed by the POST above, regardless of whether it succeeded. Clear it
      // after every attempt so a retry can never reuse a dead payload; a fresh
      // one is generated when the card is re-tokenized (or the wallet flow is
      // re-triggered) before the next submission.
      setGatewayPayload({});
      // Wallets need a new provider interaction after their nonce is consumed.
      // Keep cards selected: Card Details validates and tokenizes again on retry.
      if (
        !submissionSucceeded &&
        needsGatewayAuthorisation &&
        paymentMethod !== "CC"
      ) {
        setPaymentMethod(null);
      }
      setProcessing(false);
      resetTurnstile();
    }
  };

  const retry = () =>
    runExclusively(async () => {
      setProcessing(true);
      try {
        if (
          sessionSync.needsReplacementBeforeRetry() &&
          !(await sessionSync.replaceFailedSession())
        ) {
          return;
        }
        if (visibleDetailsSteps.includes(DetailsStep.PaymentType)) {
          setGatewayPayload({});
          navigateTo(DetailsStep.PaymentType);
        } else {
          const lastVisibleStep =
            visibleDetailsSteps[visibleDetailsSteps.length - 1];
          if (lastVisibleStep) navigateTo(lastVisibleStep);
        }
        setSubmissionError(null);
      } finally {
        setProcessing(false);
      }
    });

  // Nothing awaits Continue: a rejection would leave the button looking dead
  // with no message (#1196).
  const continueFromAmount = () =>
    runExclusively(async () => {
      setAmountStepFailed(false);
      try {
        if ((await sessionSync.openSessionForAmount()) === "already-donated") {
          return;
        }
      } catch (sessionError) {
        trackSubmissionFailure(
          sessionError,
          { form_id: formId, context: "continue-from-amount" },
          serverErrorBody(sessionError),
          null,
        );
        setAmountStepFailed(true);
        return;
      }
      next();
    });

  return {
    processing,
    submissionError,
    showPaymentError,
    amountStepFailed,
    isRecurringPromptVisible,
    continueFromAmount,
    submit: () => runExclusively(attemptSubmit),
    confirmRecurring: () => runExclusively(submitDonation),
    retry,
    manageExistingRecurring,
  };
}
